import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { toast } from 'sonner';
import { MoreHorizontal, Pencil, Plus, Search, ShieldCheck, ShieldOff } from 'lucide-react';
import Page from 'components/shared/Page';
import HistoricalCampaignStrip from 'components/shared/HistoricalCampaignStrip';
import { useAGLOCampaign, useCampaignSwitchStatus } from 'hooks/useAGLOCampaign';
import Pagination from 'components/shared/Pagination';
import ConfirmationModal from 'components/shared/ConfirmationModal';
import EmptyCard from 'components/shared/EmptyCard';
import { useAPI, useDebounce } from 'hooks';
import { getUserName, formatDate, preventSubmitWithENTER } from 'helpers';
import Locales from 'locales';
import { defaultCategories } from 'data/Pilgrims';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { Input } from 'components/ui/input';
import { TableBody, TableCell, TableHead, TableHeader, TableRow } from 'components/ui/table';
import { Badge } from 'components/ui/badge';
import { Button } from 'components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from 'components/ui/dropdown-menu';
import AddAgencyModal from './components/AddAgencyModal';
import EditAgencyModal from './components/EditAgencyModal';

const Agency = () => {
  const campaign = useAGLOCampaign();
  const [search, setSearch] = useState('');
  const debouncedSearchQuery = useDebounce(search, 1000);

  return (
    <AgencyForCampaign
      key={campaign.scope.join(':')}
      campaign={campaign}
      search={search}
      setSearch={setSearch}
      debouncedSearchQuery={debouncedSearchQuery}
    />
  );
};

const AgencyForCampaign = ({ campaign, search, setSearch, debouncedSearchQuery }) => {
  const { api } = useAPI();
  const { params: campaignParams, scope: campaignScope, isReadOnly } = campaign;
  const [cursor, setCursor] = useState('');
  const [openConfirmationModal, setOpenConfirmationModal] = useState(false);
  const [openRevokeModal, setOpenRevokeModal] = useState(false);
  const [openAddModal, setOpenAddModal] = useState(false);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const queryClient = useQueryClient();

  const fetchParams = {
    ...(search.length > 0 ? { q: debouncedSearchQuery } : { cursor }),
    ...campaignParams,
  };

  const agencyQuery = useQuery({
    queryFn: api.list('agency/', fetchParams),
    queryKey: ['agency/', debouncedSearchQuery, cursor, ...campaignScope],
    keepPreviousData: false,
  });

  const { data: agencies, isFetching } = agencyQuery;
  useCampaignSwitchStatus([agencyQuery]);

  const { data: categoriesData } = useQuery({
    queryFn: api.list('category/'),
    queryKey: ['category/'],
  });

  const categories = categoriesData
    ? categoriesData
        .filter((category) => defaultCategories.includes(category.name))
        .map((category) => ({
          ...category,
          value: category.id,
          label: category.name,
        }))
        .reverse()
    : [];

  const authorizationTone = (license) => {
    if (license.approved) {
      return 'text-[#286d47]';
    }
    if (license.requested) {
      return 'text-[#946b36]';
    }
    if (license.rejected) {
      return 'text-[#a74b54]';
    }
    return 'text-[#68796e]';
  };

  const authorizationLabel = (license) => {
    if (license.approved) {
      return 'Approuvée';
    }
    if (license.requested) {
      return 'En attente';
    }
    if (license.rejected) {
      return 'Rejetée';
    }
    return 'Aucune demande';
  };

  const { mutate: approveLicence } = useMutation({
    meta: { agloCampaignWrite: true },
    mutationFn: (agency) => api.post(`agency/${agency.id}/license_approve/`),
    onSuccess: ({ data }) => {
      const { name } = data;
      queryClient.invalidateQueries({ queryKey: ['agency/'] });
      queryClient.invalidateQueries({ queryKey: ['agency/stats/default/'] });
      toast.success(Locales.formatString(Locales.agency.toast.validate_autorisation, { name }));
    },
  });

  const { mutate: revokeLicence } = useMutation({
    meta: { agloCampaignWrite: true },
    mutationFn: (agency) => api.post(`agency/${agency.id}/license_revoke/`),
    onSuccess: ({ data }) => {
      const { name } = data;
      queryClient.invalidateQueries({ queryKey: ['agency/'] });
      queryClient.invalidateQueries({ queryKey: ['agency/stats/default/'] });
      toast.success(Locales.formatString(Locales.agency.toast.cancel_autorisation, { name }));
    },
  });

  const handleGrantAuthorization = (agency) => {
    if (isReadOnly) return;
    setOpenConfirmationModal(true);
    setSelectedItem(agency);
  };

  const handleRevokeAuthorization = (agency) => {
    if (isReadOnly) return;
    setOpenRevokeModal(true);
    setSelectedItem(agency);
  };

  const handleEdit = (agency) => {
    if (isReadOnly) return;
    setOpenEditModal(true);
    setSelectedItem(agency);
  };

  const resultString = agencies
    ? `${agencies.count} ${
        agencies.count <= 1 ? Locales.agency.agency.slice(0, -1).toLowerCase() : Locales.agency.agency.toLowerCase()
      }`
    : '-';

  return (
    <Page
      title="Liste des Sociétés"
      headerContent={<HistoricalCampaignStrip />}
      actionDisabled={isReadOnly}
      hideEmptyStats
      description="Gérez les sociétés et leurs informations"
      actionLabel="Ajouter une société"
      actionIcon={<Plus aria-hidden="true" className="h-4 w-4" />}
      onAction={() => !isReadOnly && setOpenAddModal(true)}
      removeContentStyles
      actionClassName="!bg-[#28764d] !shadow-none hover:!bg-[#246e48]"
      bodyClassName="!w-[calc(100%_-_24px)] sm:!w-[calc(100%_-_60px)] !space-y-6"
    >
      <div className="overflow-hidden rounded-xl border border-[#dce5df] bg-white">
        <div className="px-4 pt-4">
          <div className="relative">
            <Search aria-hidden="true" className="absolute left-3 top-3 h-4 w-4 text-[#7c8980]" />
            <Input
              aria-label="Rechercher une société"
              placeholder="Rechercher une société..."
              className="h-10 w-full rounded-md border-[#d8e1da] pl-9"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={preventSubmitWithENTER}
            />
          </div>
        </div>
        <div className="px-4 py-3 text-[13px] font-semibold text-[#405345]">{agencies ? resultString : '–'}</div>
        {!agencies ? (
          <EmptyCard />
        ) : (
          <>
            <div className="overflow-x-auto border-t border-[#dce5df]">
              <table className="w-full min-w-[1050px] table-fixed text-sm">
                <colgroup>
                  <col className="w-[18%]" />
                  <col className="w-[13%]" />
                  <col className="w-[23%]" />
                  <col className="w-[11%]" />
                  <col className="w-[8%]" />
                  <col className="w-[12%]" />
                  <col className="w-[9%]" />
                  <col className="w-[6%]" />
                </colgroup>
                <TableHeader className="bg-[#f5f7f6]">
                  <TableRow className="hover:bg-transparent">
                    {['Nom', 'IFU', 'Administrateur principal', 'Statut'].map((heading) => (
                      <TableHead key={heading} className="h-[46px] text-xs text-[#66766c]">
                        {heading}
                      </TableHead>
                    ))}
                    {['Badge', 'Autorisation', 'Création', 'Actions'].map((heading) => (
                      <TableHead key={heading} className="h-[46px] text-xs text-[#66766c]">
                        {heading}
                      </TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {agencies.map((agency) => (
                    <TableRow key={agency.id} className="h-[65px] border-[#edf0ee] hover:bg-[#fafcfb]">
                      <TableCell className="whitespace-normal font-semibold text-[#2e4636]">
                        <span className="line-clamp-2 break-words" title={agency.name}>
                          {agency.name}
                        </span>
                        {agency.merger.approved && (
                          <sub
                            className="italic text-green-700"
                            title={`${Locales.agency.merger} ${agency.merger.master_name}`}
                          >
                            ↳ {agency.merger.master_name}
                          </sub>
                        )}
                      </TableCell>
                      <TableCell className="text-[13px] tabular-nums">{agency.ifu}</TableCell>
                      <TableCell className="max-w-0 whitespace-normal">
                        {agency.admin ? (
                          <div className="min-w-0">
                            <div
                              className="line-clamp-2 break-words font-medium"
                              title={getUserName(agency.admin.first_name, agency.admin.last_name)}
                            >
                              {getUserName(agency.admin.first_name, agency.admin.last_name)}
                            </div>
                            <div className="truncate text-xs text-[#748078]" title={agency.admin.email}>
                              {agency.admin.email}
                            </div>
                          </div>
                        ) : (
                          <span className="text-[13px] text-[#7b867e]">Non renseigné</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant={agency.admin?.is_verified && agency.admin?.is_active ? 'success' : 'destructive'}
                          className="gap-1"
                        >
                          {agency.admin?.is_verified && agency.admin?.is_active ? 'Activé' : 'Désactivé'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {agency.badge ? (
                          <a href={agency.badge} download>
                            <img className="border-card avatar" src={agency.badge} alt="badge" />
                          </a>
                        ) : (
                          <span className="text-[#8a978f]">–</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <span className={`text-xs ${authorizationTone(agency.license)}`}>
                          {authorizationLabel(agency.license)}
                        </span>
                      </TableCell>
                      <TableCell className="text-[13px] tabular-nums">{formatDate(agency.created)}</TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              aria-label={`Actions pour ${agency.name}`}
                              className="!h-8 !w-8 border border-transparent text-[#708075] shadow-none hover:border-[#dce7df] hover:bg-[#edf4ef] hover:text-[#2f6746]"
                            >
                              <MoreHorizontal className="h-4 w-4" aria-hidden="true" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent
                            align="end"
                            className="w-56 border-[#dfe6e1] p-1 shadow-[0_6px_24px_#182d201c]"
                          >
                            <DropdownMenuGroup>
                              <DropdownMenuItem
                                disabled={isReadOnly}
                                onClick={() => handleEdit(agency)}
                                className="gap-2 text-[13px] text-[#3e5144]"
                              >
                                <Pencil aria-hidden="true" className="h-4 w-4 text-[#728278]" />
                                Éditer
                              </DropdownMenuItem>
                              {agency.license.approved && (
                                <DropdownMenuItem
                                  disabled={isReadOnly}
                                  onClick={() => handleRevokeAuthorization(agency)}
                                  className="gap-2 text-[13px] text-[#3e5144]"
                                >
                                  <ShieldOff aria-hidden="true" className="h-4 w-4 text-[#728278]" />
                                  {Locales.agency.button.remove}
                                </DropdownMenuItem>
                              )}
                              {!agency.license.approved && (
                                <DropdownMenuItem
                                  disabled={isReadOnly}
                                  onClick={() => handleGrantAuthorization(agency)}
                                  className="gap-2 text-[13px] text-[#3e5144]"
                                >
                                  <ShieldCheck aria-hidden="true" className="h-4 w-4 text-[#728278]" />
                                  {Locales.agency.button.authorize}
                                </DropdownMenuItem>
                              )}
                            </DropdownMenuGroup>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </table>
            </div>
            <Pagination data={agencies} isFetching={isFetching} setCursor={setCursor} />
          </>
        )}
      </div>

      {selectedItem && (
        <>
          {/* Grant Authorization Modal */}

          <ConfirmationModal
            isOpen={openConfirmationModal}
            setOpenModal={setOpenConfirmationModal}
            title={Locales.agency.give_autorization}
            confirmBtnLabel={Locales.agency.confirm_authorization}
            message={
              <div>
                <p>
                  {Locales.formatString(Locales.agency.make_autorisation_request, {
                    name: <strong>{selectedItem.name}</strong>,
                  })}
                </p>
                <p>{Locales.agency.after_giving_autorization}</p>
              </div>
            }
            onConfirm={() => !isReadOnly && approveLicence(selectedItem)}
          />

          {/* Revoke Authorization Modal */}

          <ConfirmationModal
            isOpen={openRevokeModal}
            setOpenModal={setOpenRevokeModal}
            title={Locales.agency.remove_autorization}
            confirmBtnLabel={Locales.agency.button.remove}
            confirmBtnVariant="destructive"
            message={
              <div>
                <p>
                  {Locales.formatString(Locales.agency.make_remove_autorisation_request, {
                    name: <strong>{selectedItem.name}</strong>,
                  })}
                </p>

                <p>{Locales.agency.after_removing_autorization}</p>
              </div>
            }
            onConfirm={() => !isReadOnly && revokeLicence(selectedItem)}
          />

          {openEditModal && (
            <EditAgencyModal
              key={selectedItem.id}
              isOpen={openEditModal}
              isReadOnly={isReadOnly}
              categories={categories}
              onClose={() => setOpenEditModal(false)}
              agency={selectedItem}
            />
          )}
        </>
      )}

      {openAddModal && (
        <AddAgencyModal
          isReadOnly={isReadOnly}
          isOpen={openAddModal}
          categories={categories}
          onClose={() => setOpenAddModal(false)}
        />
      )}
    </Page>
  );
};

AgencyForCampaign.propTypes = {
  campaign: PropTypes.shape({
    params: PropTypes.shape({}).isRequired,
    scope: PropTypes.array.isRequired,
    isReadOnly: PropTypes.bool.isRequired,
  }).isRequired,
  search: PropTypes.string.isRequired,
  setSearch: PropTypes.func.isRequired,
  debouncedSearchQuery: PropTypes.string.isRequired,
};

export default Agency;
